// @ts-nocheck
// React hook for fetching server-rendered chapter prose.
//
// Part of the item 1.1 (move chapter content out of bundle) foundation:
// chapters whose prose has been migrated to the book_content table use
// this hook instead of inline JSX. Chapters that haven't been migrated
// yet keep their existing inline JSX in App.tsx — that's fine; the two
// styles co-exist during the migration period.
//
// The hook calls the SECURITY DEFINER RPC `get_chapter_content` which
// enforces:
//   - Chapter 1 is always allowed (free preview)
//   - Chapters 2-12 require user_has_access() = true
// So this hook can be called unconditionally; the server is the gate.
//
// Cache strategy: we keep results in a module-level Map keyed by
// (chapter, version) so navigating between chapters is instant after
// the first fetch. Version comes back in the response — if the admin
// re-uploads a chapter, the cache will simply hold a stale copy until
// the user reloads. Acceptable for a small, low-frequency edit workflow.

import { useEffect, useState } from "react";
import { supabase } from "./supabase";

export type ChapterContent = {
  prose_html: string;
  widget_anchors: Array<{ id: string; after_selector?: string }>;
  version: number;
};

type State =
  | { status: "loading"; content: null; error: null }
  | { status: "ready"; content: ChapterContent; error: null }
  | { status: "empty"; content: null; error: null }
  | { status: "error"; content: null; error: string };

const cache = new Map<number, ChapterContent>();

export function useChapterContent(chapter: number, enabled: boolean = true): State {
  const [state, setState] = useState<State>({
    status: "loading",
    content: null,
    error: null,
  });

  useEffect(() => {
    if (!enabled) {
      setState({ status: "empty", content: null, error: null });
      return;
    }

    // Serve from cache if we already have this chapter.
    const cached = cache.get(chapter);
    if (cached) {
      setState({ status: "ready", content: cached, error: null });
      return;
    }

    let cancelled = false;
    setState({ status: "loading", content: null, error: null });

    (async () => {
      // Local preview only: when `npm start` serves public/dev/chapter-N.html
      // (git-ignored), render that file instead of the production row so a
      // chapter edit can be checked in the browser before it is merged and
      // synced. The CRA dev server answers missing files with index.html,
      // hence the doctype guard.
      if (process.env.NODE_ENV === "development") {
        try {
          const r = await fetch(`/dev/chapter-${chapter}.html`, { cache: "no-store" });
          if (r.ok) {
            const t = await r.text();
            if (t.trim().startsWith("<") && !/<!doctype|<html/i.test(t.slice(0, 200))) {
              if (cancelled) return;
              setState({ status: "ready", content: { prose_html: t, widget_anchors: [], version: 0 }, error: null });
              return;
            }
          }
        } catch {
          /* fall through to the server */
        }
      }
      const { data, error } = await supabase.rpc("get_chapter_content", {
        _chapter: chapter,
      });
      if (cancelled) return;

      if (error) {
        setState({ status: "error", content: null, error: error.message });
        return;
      }

      const row = Array.isArray(data) ? data[0] : data;
      if (!row || !row.prose_html) {
        // No content row yet for this chapter — caller should fall back
        // to whatever inline JSX exists in the legacy App.tsx blocks.
        setState({ status: "empty", content: null, error: null });
        return;
      }

      const content: ChapterContent = {
        prose_html: row.prose_html,
        widget_anchors: row.widget_anchors ?? [],
        version: row.version,
      };
      cache.set(chapter, content);
      setState({ status: "ready", content, error: null });
    })();

    return () => {
      cancelled = true;
    };
  }, [chapter, enabled]);

  return state;
}
